const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;
const MONTH_PER_YEAR = 12;

export const TIME_DAY_TYPES = {
    AT: 'At',
    BETWEEN: 'Between',
    ANY: 'Any',
};

export const createDate = function (day, month, year) {
    return {
        day,
        month,
        year,
    };
};

export const createTime = function (hour, minute, amPm) {
    return {
        hour,
        minute,
        amPm,
    };
};

const normalizeHour = function (hour, amPm) {
    const intHour = parseInt(hour, 10);
    const baselineHour = intHour === 12 ? 0 : intHour;

    return amPm === 'AM' ? baselineHour : baselineHour + 12;
};

const isTimeAfter = function (time1, time2) {
    const normalizedHour1 = normalizeHour(time1.hour, time1.amPm);
    const normalizedMinute1 = parseInt(time1.minute, 10);

    const normalizedHour2 = normalizeHour(time2.hour, time2.amPm);
    const normalizedMinute2 = parseInt(time2.minute, 10);

    return normalizedHour1 > normalizedHour2
            || (normalizedHour1 === normalizedHour2 && normalizedMinute1 > normalizedMinute2);
};

const isTimeEqual = function (time1, time2) {
    return time1.hour?.toString() === time2.hour?.toString() && time1.minute?.toString() === time2.minute?.toString() && time1.amPm === time2.amPm;
};

const normalizeWaitDelay = function (waitDelay, waitDelayType) {
    const units = parseInt(waitDelay, 10);
    let minutes = 0;

    if (waitDelayType === 'Minute') {
        minutes = units;
    } else if (waitDelayType === 'Hour') {
        minutes = units * MINUTES_PER_HOUR;
    } else if (waitDelayType === 'Day') {
        minutes = units * HOURS_PER_DAY * MINUTES_PER_HOUR;
    } else if (waitDelayType === 'Week') {
        minutes = units * DAYS_PER_WEEK * HOURS_PER_DAY * MINUTES_PER_HOUR;
    } else if (waitDelayType === 'Month') {
        minutes = units * DAYS_PER_MONTH * HOURS_PER_DAY * MINUTES_PER_HOUR;
    } else if (waitDelayType === 'Year') {
        minutes = units * MONTH_PER_YEAR * DAYS_PER_MONTH * HOURS_PER_DAY * MINUTES_PER_HOUR;
    }

    return minutes;
};

const isWaitDelayBefore = function (waitDelay1, waitDelayType1, waitDelay2, waitDelayType2) {
    const normalized1 = normalizeWaitDelay(waitDelay1, waitDelayType1);
    const normalized2 = normalizeWaitDelay(waitDelay2, waitDelayType2);

    return normalized1 < normalized2;
};

const isWaitDelayAfter = function (waitDelay1, waitDelayType1, waitDelay2, waitDelayType2) {
    const normalized1 = normalizeWaitDelay(waitDelay1, waitDelayType1);
    const normalized2 = normalizeWaitDelay(waitDelay2, waitDelayType2);

    return normalized1 > normalized2;
};

export const isAfterOrEqualToNext = function (
    waitDelay,
    waitDelayType,
    waitDelayModifierType,
    startTime,
    nextWaitDelay,
    nextWaitDelayType,
    nextWaitDelayModifierType,
    nextStartTime,
) {
    let afterOrEqualToNext = true; // pessimistic

    if (waitDelay === nextWaitDelay && waitDelayType === nextWaitDelayType && waitDelayModifierType === nextWaitDelayModifierType) {
        afterOrEqualToNext = isTimeAfter(startTime, nextStartTime) || isTimeEqual(startTime, nextStartTime);
    } else if (waitDelayType === 'NoDelay' && nextWaitDelayType === 'NoDelay') {
        afterOrEqualToNext = isTimeAfter(startTime, nextStartTime) || isTimeEqual(startTime, nextStartTime);
    } else if (waitDelayType === 'NoDelay' && nextWaitDelayModifierType === 'Before') {
        afterOrEqualToNext = true;
    } else if (waitDelayType === 'NoDelay' && nextWaitDelayModifierType === 'After') {
        afterOrEqualToNext = false;
    } else if (waitDelayModifierType === 'Before' && nextWaitDelayType === 'NoDelay') {
        afterOrEqualToNext = false;
    } else if (waitDelayModifierType === 'After' && nextWaitDelayType === 'NoDelay') {
        afterOrEqualToNext = true;
    } else if (waitDelayModifierType === 'Before' && nextWaitDelayModifierType === 'Before') {
        afterOrEqualToNext = isWaitDelayBefore(waitDelay, waitDelayType, nextWaitDelay, nextWaitDelayType);
    } else if (waitDelayModifierType === 'Before' && nextWaitDelayModifierType === 'After') {
        afterOrEqualToNext = false;
    } else if (waitDelayModifierType === 'After' && nextWaitDelayModifierType === 'Before') {
        afterOrEqualToNext = true;
    } else if (waitDelayModifierType === 'After' && nextWaitDelayModifierType === 'After') {
        afterOrEqualToNext = isWaitDelayAfter(waitDelay, waitDelayType, nextWaitDelay, nextWaitDelayType);
    }

    return afterOrEqualToNext;
};

export const isBeforeOrEqualToPrevious = function (waitDelayNow, waitDelayTypeNow, waitDelayModifierTypeNow, startTimeNow, prevTimer) {
    const prevCellValue = prevTimer?.value;
    const { waitDelay } = prevCellValue;
    const { waitDelayType } = prevCellValue;
    const { waitDelayModifierType } = prevCellValue;
    const prevStartTime = createTime(prevCellValue.startHour, prevCellValue.startMinute, prevCellValue.startAmPm);

    return isAfterOrEqualToNext(waitDelay?.toString(), waitDelayType, waitDelayModifierType, prevStartTime, waitDelayNow, waitDelayTypeNow?.toString(), waitDelayModifierTypeNow, startTimeNow);
};

export const isTimeBefore = function (time1, time2) {
    const normalizedHour1 = normalizeHour(time1.hour, time1.amPm);
    const normalizedMinute1 = parseInt(time1.minute, 10);

    const normalizedHour2 = normalizeHour(time2.hour, time2.amPm);
    const normalizedMinute2 = parseInt(time2.minute, 10);

    return normalizedHour1 < normalizedHour2
            || (normalizedHour1 === normalizedHour2 && normalizedMinute1 < normalizedMinute2);
};

export const isValidMinute = function (minute) {
    const num = Number(minute);

    if (Number.isNaN(num)) {
        return false;
    }

    return num % 15 === 0 && num >= 0 && num <= 45;
};

export const cleanUpTimeFields = function (cell) {
    if (cell.value.timeOfDayType === TIME_DAY_TYPES.ANY) {
        cell.value.startHour = '08';
        cell.value.startMinute = '00';
        cell.value.startAmPm = 'AM';
        delete cell.value.stopHour;
        delete cell.value.stopMinute;
        delete cell.value.stopAmPm;
    } else if (cell.value.timeOfDayType === TIME_DAY_TYPES.AT) {
        delete cell.value.stopHour;
        delete cell.value.stopMinute;
        delete cell.value.stopAmPm;
    }
};

export const getDay = function (day) {
    return day ?? new Date().getDate().valueOf();
};

export const getMonth = function (month) {
    return month ? month - 1 : new Date().getMonth().valueOf();
};

export const getYear = function (year) {
    return year ?? new Date().getFullYear().valueOf();
};
